import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpError, PriceCurveResponse } from '../../../core/clients/omni-core/models';
import { Api } from '../../../core/clients/omni-core/api';
import { getPriceCurve, GetPriceCurve$Params } from '../../../core/clients/omni-core/functions';
import {
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns';
import { de } from 'date-fns/locale';
import { environment } from '../../../../environments/environment';
import { AnalysisGranularity } from '../chart-config/util';

export interface PriceCurveState {
  id: string;
  data: PriceCurveResponse | null;

  isLoading: boolean;
  error: HttpError | unknown | null;
}

export interface PriceFilter {
  granularity: AnalysisGranularity;
  date: Date;
  showEurMwh: boolean;
  view: 'graph' | 'table';
  /**
   * yyyy string
   * Leave empty string to disable compare historic year
   */
  compareHistoricYear: string;
}

export const getPriceCurveGranularity = (
  filter: PriceFilter,
): GetPriceCurve$Params['granularity'] => {
  switch (filter.granularity) {
    case 'YEAR':
    case 'MONTH':
      return 'DAY';
    case 'WEEK':
    case 'DAY':
      return 'HOUR';
  }
};

export const getInitialFilter = (): PriceFilter => ({
  granularity: 'YEAR',
  date: new Date(),
  showEurMwh: false,
  view: 'graph',
  compareHistoricYear: '',
});

export const isFilterEqual = (a: PriceFilter, b: PriceFilter): boolean =>
  filterToId(a) === filterToId(b);

export const filterToId = (filter: PriceFilter): string => {
  switch (filter.granularity) {
    case 'YEAR':
      return `YEAR-${filter.date.getFullYear()}`;
    case 'MONTH':
      return `MONTH-${format(filter.date, 'yyyy-MM')}`;
    case 'WEEK':
      return `WEEK-${format(filter.date, 'yyyy-ww')}`;
    case 'DAY':
      return `DAY-${format(filter.date, 'yyyy-MM-dd')}`;
  }
};

export const getSelectedFrom = (filter: PriceFilter): Date => {
  switch (filter.granularity) {
    case 'YEAR':
      return startOfYear(filter.date);
    case 'MONTH':
      return startOfMonth(filter.date);
    case 'WEEK':
      return startOfWeek(filter.date, { locale: de });
    default:
      return filter.date;
  }
};

export const getSelectedTo = (filter: PriceFilter): Date => {
  switch (filter.granularity) {
    case 'YEAR':
      return endOfYear(filter.date);
    case 'MONTH':
      return endOfMonth(filter.date);
    case 'WEEK':
      return endOfWeek(filter.date, { locale: de });
    default:
      return filter.date;
  }
};

@Injectable({
  providedIn: 'root',
})
export class PriceStore {
  private api = inject(Api);

  filter = signal<PriceFilter>(getInitialFilter());

  #data = signal<Record<string, PriceCurveState>>({});
  data = computed(this.#data);

  #queue = signal<{ id: string; status: 'OPEN' | 'IN_PROGRESS'; payload: PriceFilter }[]>([]);
  private open = computed(() => this.#queue().filter((q) => q.status === 'OPEN'));
  private isProcessing = computed(() => this.#queue().some((q) => q.status === 'IN_PROGRESS'));

  private patchItem(id: string, newData: Partial<PriceCurveState>) {
    this.#data.update((data) => ({ ...data, [id]: { ...data[id], ...newData } }));
  }

  get(filter: PriceFilter): PriceCurveState | null {
    const id = filterToId(filter);
    return this.#data()[id] ?? null;
  }

  async load(filter: PriceFilter): Promise<void> {
    const id = filterToId(filter);
    this.#data.update((data) => ({
      ...data,
      [id]: {
        id,
        data: null,
        isLoading: true,
        error: null,
      },
    }));
    try {
      const res = await this.api.invoke(getPriceCurve, {
        granularity: getPriceCurveGranularity(filter),
        from: format(getSelectedFrom(filter), 'yyyy-MM-dd'),
        to: format(getSelectedTo(filter), 'yyyy-MM-dd'),
      });
      this.patchItem(id, { data: res });
    } catch (error) {
      this.patchItem(id, { error });
    } finally {
      this.patchItem(id, { isLoading: false });
    }
  }

  async queue(filter: PriceFilter): Promise<void> {
    const id = filterToId(filter);
    if (this.#queue().some((entry) => filterToId(entry.payload) === id)) {
      return;
    }
    this.#queue.update((q) => [...q, { id: crypto.randomUUID(), payload: filter, status: 'OPEN' }]);
    if (!this.isProcessing()) {
      this.processQueue();
    }
  }

  private async processQueue(): Promise<void> {
    if (this.isProcessing() || this.open().length === 0) {
      return;
    }
    const queue = this.#queue();
    const entry = queue[0];
    entry.status = 'IN_PROGRESS';
    this.#queue.update((q) => [entry, ...q.filter((e) => e.id !== entry.id)]);
    if (!this.get(entry.payload)) {
      await this.load(entry.payload);
    }
    await new Promise((resolve) => setTimeout(resolve, environment.analysis.price.queueDelayMs));
    this.#queue.update((q) => q.filter((e) => e.id !== entry.id));
    this.processQueue();
  }

  onChangeFilter() {
    const filter = this.filter();
    if (this.get(filter)) {
      return;
    }
    return this.load(this.filter());
  }

  reset() {
    this.#data.set({});
  }
}
